import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useMemo, useState } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

import type {
  InsuranceCardReaderOutput,
  MedicareCardReaderOutput,
  PreClinicStep,
  PreClinicStepsContent,
} from "@/api/generated/models";
import type { ClientEventModel } from "@/api/model";
import { AssistantMessageTypes } from "@/types";

export const PRE_CLINIC_STEP_PAGES = {
  insurance: ["has_insurance", "photo", "manual"],
  medicare_insurance: ["medicare_photo", "medicare_manual"],
  affiliation: ["has_affiliation", "confirm_details", "date_of_birth"],
  demographics: ["demographics"],
  current_state: ["state"],
} as Record<PreClinicStep, string[]>;

export type PreClinicPage =
  (typeof PRE_CLINIC_STEP_PAGES)[PreClinicStep][number];

export const isKnownPreClinicStep = (step: string): step is PreClinicStep =>
  Boolean(PRE_CLINIC_STEP_PAGES[step as PreClinicStep]);

const DEFAULT_PAGE: PreClinicPage = PRE_CLINIC_STEP_PAGES.insurance[0];

export type PreClinicCollected = {
  hasInsurance?: boolean;
  hasAffiliation?: boolean;
  insurance: InsuranceCardReaderOutput | null;
  medicareCard: MedicareCardReaderOutput | null;
};

const pagesForStep = (
  step: PreClinicStep,
  collected: PreClinicCollected,
  skipDateOfBirth: boolean,
): PreClinicPage[] => {
  const pages = PRE_CLINIC_STEP_PAGES[step];
  if (step === "insurance") {
    return collected.hasInsurance === false ? ["has_insurance"] : pages;
  }
  if (step === "affiliation") {
    const maybeWithoutDob = skipDateOfBirth
      ? pages.filter((page) => page !== "date_of_birth")
      : pages;
    if (collected.hasAffiliation === false) {
      return maybeWithoutDob.filter((page) => page !== "confirm_details");
    }
    if (collected.hasAffiliation === true) {
      return maybeWithoutDob.filter((page) => page !== "date_of_birth");
    }
    return maybeWithoutDob;
  }
  return [...pages];
};

const EMPTY_COLLECTED: PreClinicCollected = {
  insurance: null,
  medicareCard: null,
};

export type PreClinicProgress = {
  resolvedSteps: PreClinicStep[];
};

const EMPTY_PROGRESS: PreClinicProgress = {
  resolvedSteps: [],
};

export type ActivePreClinic = {
  conversationId: string;
  clinicRequestId: string;
  steps: PreClinicStep[];
  page: PreClinicPage;
  collected: PreClinicCollected;
  /**
   * Whether finishing these steps has to run a generalist turn.
   *
   * True when the generalist asked for them mid-conversation: it is waiting on the answers and
   * nothing else would wake it. A clinic request's own steps end at a card the user taps, so
   * they leave the conversation alone.
   */
  resumesTurn: boolean;
};

export interface PreClinicStepsStore {
  progressByClinicRequestId: Record<string, PreClinicProgress>;
  active: ActivePreClinic | null;
  getProgress: (clinicRequestId: string | undefined) => PreClinicProgress;
  markStepResolved: (clinicRequestId: string, step: PreClinicStep) => void;
  startPreClinic: (
    conversationId: string,
    clinicRequestId: string,
    steps: PreClinicStep[],
    resumesTurn?: boolean,
  ) => void;
  clearPreClinic: () => void;
  goToPage: (page: PreClinicPage) => void;
  setCollected: (collected: Partial<PreClinicCollected>) => void;
}

export const usePreClinicStepsStore = create<PreClinicStepsStore>()(
  persist(
    (set, get) => ({
      progressByClinicRequestId: {},
      active: null,
      getProgress: (clinicRequestId) => {
        if (!clinicRequestId) return EMPTY_PROGRESS;
        return (
          get().progressByClinicRequestId[clinicRequestId] ?? EMPTY_PROGRESS
        );
      },
      markStepResolved: (clinicRequestId, step) =>
        set((state) => {
          const prev =
            state.progressByClinicRequestId[clinicRequestId] ?? EMPTY_PROGRESS;
          if (prev.resolvedSteps.includes(step)) return state;
          return {
            progressByClinicRequestId: {
              ...state.progressByClinicRequestId,
              [clinicRequestId]: {
                ...prev,
                resolvedSteps: [...prev.resolvedSteps, step],
              },
            },
          };
        }),
      startPreClinic: (
        conversationId,
        clinicRequestId,
        steps,
        resumesTurn = false,
      ) =>
        set((state) => {
          if (
            state.active?.conversationId === conversationId &&
            state.active?.clinicRequestId === clinicRequestId
          ) {
            return state;
          }
          return {
            active: {
              conversationId,
              clinicRequestId,
              steps,
              page: DEFAULT_PAGE,
              collected: { insurance: null, medicareCard: null },
              resumesTurn,
            },
          };
        }),
      clearPreClinic: () => set({ active: null }),
      goToPage: (page) =>
        set((state) =>
          state.active ? { active: { ...state.active, page } } : state,
        ),
      setCollected: (collected) =>
        set((state) =>
          state.active
            ? {
                active: {
                  ...state.active,
                  collected: { ...state.active.collected, ...collected },
                },
              }
            : state,
        ),
    }),
    {
      name: "pre-clinic-steps-storage",
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({
        progressByClinicRequestId: state.progressByClinicRequestId,
      }),
    },
  ),
);

export const selectPreClinicPages =
  (steps: PreClinicStep[], skipDateOfBirth = false) =>
  ({ active }: Pick<PreClinicStepsStore, "active">): PreClinicPage[] =>
    steps.flatMap((step) =>
      pagesForStep(step, active?.collected ?? EMPTY_COLLECTED, skipDateOfBirth),
    );

export const selectIsPageAnswered =
  (page: PreClinicPage) =>
  ({ active }: Pick<PreClinicStepsStore, "active">): boolean => {
    const collected = active?.collected ?? EMPTY_COLLECTED;
    switch (page) {
      case "has_insurance":
        return collected.hasInsurance !== undefined;
      case "photo":
        return collected.insurance !== null;
      case "medicare_photo":
        return collected.medicareCard !== null;
      case "medicare_manual":
        return false;
      case "has_affiliation":
        return collected.hasAffiliation !== undefined;
      default:
        return false;
    }
  };

export const isPreClinicFlowResolved = (
  requiredSteps: PreClinicStep[],
  progress: PreClinicProgress | undefined,
): boolean => {
  const resolved = progress?.resolvedSteps ?? [];
  return requiredSteps.every((step) => resolved.includes(step));
};

export const useHasPreClinicStepsStoreHydrated = (): boolean => {
  const [hasHydrated, setHasHydrated] = useState(() =>
    usePreClinicStepsStore.persist.hasHydrated(),
  );
  useEffect(() => {
    const unsub = usePreClinicStepsStore.persist.onFinishHydration(() =>
      setHasHydrated(true),
    );
    if (usePreClinicStepsStore.persist.hasHydrated()) {
      setHasHydrated(true);
    }
    return unsub;
  }, []);
  return hasHydrated;
};

/**
 * Start the pre-clinic panel from a PRE_CLINIC_STEPS event, before any clinic request exists.
 *
 * `clinician_connection` emits this when a gate needs a profile field we do not have. The steps
 * write straight to the UserProfile, which the next generalist turn reads when it seeds facts.
 * The event id stands in for the clinic request id as the progress key: there is no request yet,
 * and the key only has to be stable for this collection.
 *
 * The caller passes `conversationId` instead of this reading it from a hook. `useInfiniteMessages`
 * imports the store barrel, so importing `@/hooks/conversation` here closes a module cycle.
 */
export function useEarlyPreClinicSession(
  allMessages: ClientEventModel[],
  conversationId: string,
) {
  const startPreClinic = usePreClinicStepsStore((s) => s.startPreClinic);

  // The most recent request, not the last message: the generalist answers the user in the same
  // turn it calls the tool, so its reply lands after the event. Scoped to this conversation,
  // because a group stream prepends the other conversations' messages to the same list.
  const { requestId, steps } = useMemo(() => {
    const request = allMessages.findLast(
      (message) =>
        message.type === AssistantMessageTypes.PRE_CLINIC_STEPS &&
        message.conversationId === conversationId,
    );
    const content = request?.content as PreClinicStepsContent | undefined;
    return {
      requestId: request?.id,
      steps: (content?.steps ?? []).filter(isKnownPreClinicStep),
    };
  }, [allMessages, conversationId]);
  const hasSteps = steps.length > 0;

  // The panel clears itself once every step is resolved, and progress is keyed by the event id,
  // so re-activating an answered request closes it again rather than re-asking.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `steps` is read at call time by design
  useEffect(() => {
    if (!hasSteps || !requestId || !conversationId) return;
    startPreClinic(conversationId, requestId, steps, true);
  }, [conversationId, requestId, hasSteps, startPreClinic]);
}
